// frontend/src/components/UsageIndicator.tsx

import React from 'react';
import { useUsage } from '../contexts/UsageContext';
import { getStatusColor } from '../types/paywall';

/**
 * UsageIndicator Component
 *
 * Displays user's current credit balance and status
 * Shown in UserProfile dropdown
 *
 * Features:
 * - Credits remaining with color-coded indicator
 * - Visual progress bar
 * - Trial expiration countdown (if applicable)
 * - Responsive to usage state changes
 */
export const UsageIndicator: React.FC = () => {
  const {
    creditsRemaining,
    totalCredits,
    isTrialActive,
    trialDaysRemaining,
    isProActive,
    isTrialExpired,
    status,
    isLoading,
    error,
  } = useUsage();

  // Calculate percentage and color
  const percentage = totalCredits > 0 ? (creditsRemaining / totalCredits) * 100 : 0;
  const statusColor = getStatusColor(creditsRemaining, totalCredits);

  // Don't render if loading or error
  if (isLoading) {
    return (
      <div className="usage-indicator">
        <div className="usage-indicator__loading">Loading usage...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-indicator">
        <div className="usage-indicator__error">Failed to load usage</div>
      </div>
    );
  }

  // Status color mapping to CSS classes
  const colorClass = {
    green: 'usage-indicator--healthy',
    yellow: 'usage-indicator--warning',
    red: 'usage-indicator--critical',
  }[statusColor];

  // Determine status text and styling based on user state
  let statusText = '';
  let statusClass = 'usage-indicator__status';

  if (isTrialActive && trialDaysRemaining !== null) {
    // Active trial with countdown
    statusText = trialDaysRemaining === 0
      ? 'Trial expires today'
      : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left`;
  } else if (isProActive) {
    // Pro subscription
    statusText = 'Pro Plan';
    statusClass += ' usage-indicator__status--pro';
  } else if (isTrialExpired) {
    // Expired trial (blocked from features)
    statusText = 'Trial Expired';
    statusClass += ' usage-indicator__status--expired';
  } else if (status === 'inactive') {
    // Inactive account
    statusText = 'Inactive';
    statusClass += ' usage-indicator__status--expired';
  } else {
    // Fallback: Active trial without expiry info
    statusText = 'Free Trial';
  }

  return (
    <div className={`usage-indicator ${colorClass}`}>
      {/* Header */}
      <div className="usage-indicator__header">
        <span className="usage-indicator__label">Credits</span>
        <span className={statusClass}>{statusText}</span>
      </div>

      {/* Credits Display */}
      <div className="usage-indicator__credits">
        <span className="usage-indicator__credits-current">{creditsRemaining.toLocaleString()}</span>
        <span className="usage-indicator__credits-separator"> / </span>
        <span className="usage-indicator__credits-total">{totalCredits.toLocaleString()}</span>
      </div>

      {/* Progress Bar */}
      <div className="usage-indicator__progress-container">
        <div
          className="usage-indicator__progress-bar"
          style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
          role="progressbar"
          aria-valuenow={creditsRemaining}
          aria-valuemin={0}
          aria-valuemax={totalCredits}
        />
      </div>

    </div>
  );
};

export default UsageIndicator;
